/**
 * Утилита для обработки ошибок API
 */

/**
 * Извлекает читаемое сообщение об ошибке из ответа API
 * @param {Object} error - Объект ошибки
 * @returns {string} - Читаемое сообщение об ошибке
 */
export function getErrorMessage(error) {
  // Если это строка, возвращаем как есть
  if (typeof error === 'string') {
    return error;
  }

  // Если это объект с полем detail
  if (error?.detail) {
    // Если detail - это строка
    if (typeof error.detail === 'string') {
      return error.detail;
    }

    // Если detail - это массив объектов (FastAPI validation errors)
    if (Array.isArray(error.detail)) {
      return error.detail.map(err => {
        if (typeof err === 'string') {
          return err;
        }
        
        // Обрабатываем объекты ошибок валидации
        if (typeof err === 'object' && err !== null) {
          const field = err.loc ? err.loc.join('.') : 'поле';
          const message = err.msg || 'Ошибка валидации';
          return `${field}: ${message}`;
        }
        
        return 'Ошибка валидации';
      }).join('; ');
    }

    // Если detail - это объект
    if (typeof error.detail === 'object' && error.detail !== null) {
      return error.detail.msg || error.detail.message || 'Ошибка валидации';
    }
  }

  // Если есть поле message
  if (error?.message) {
    return error.message;
  }

  // Если это response объект
  if (error?.response?.data) {
    return getErrorMessage(error.response.data);
  }

  // Fallback
  return 'Произошла неизвестная ошибка';
}

/**
 * Обрабатывает ошибку API и возвращает строку для отображения
 * @param {Object} error - Объект ошибки от API
 * @returns {string} - Готовое сообщение для пользователя
 */
export function handleApiError(error) {
  if (!error) {
    return 'Произошла неизвестная ошибка';
  }

  // Обрабатываем HTTP статусы
  if (error.response?.status) {
    switch (error.response.status) {
      case 400:
        return getErrorMessage(error.response.data) || 'Некорректный запрос.';
      case 401:
        return 'Ошибка авторизации. Войдите в систему заново.';
      case 403:
        return 'Недостаточно прав для выполнения операции.';
      case 404:
        return 'Запрашиваемый ресурс не найден.';
      case 422:
        return getErrorMessage(error.response.data) || 'Ошибка валидации данных.';
      case 500:
        return 'Ошибка сервера. Попробуйте позже.';
      default:
        return getErrorMessage(error.response.data) || `Ошибка сервера (${error.response.status})`;
    }
  }

  // Обрабатываем сетевые ошибки
  if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
    return 'Ошибка сети. Проверьте подключение к интернету.';
  }

  // Обрабатываем timeout
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'Превышено время ожидания. Попробуйте позже.';
  }

  // Возвращаем сообщение из ошибки
  return getErrorMessage(error) || 'Произошла неизвестная ошибка';
}

/**
 * Проверяет, является ли ошибка ошибкой валидации
 * @param {Object} error - Объект ошибки
 * @returns {boolean}
 */
export function isValidationError(error) {
  return error?.response?.status === 422 || 
         (error?.detail && Array.isArray(error.detail));
}

/**
 * Проверяет, является ли ошибка ошибкой авторизации
 * @param {Object} error - Объект ошибки
 * @returns {boolean}
 */
export function isAuthError(error) {
  return error?.response?.status === 401 || error?.response?.status === 403;
}


